define('models/User',[
    'lib/knockout',
    'lib/lodash4',
    'moment',
    'services/liveDataService',
    'services/timeService',
    'services/userStatusService',
    'util/ObservableModel'
], function (
    ko,
    _,
    moment,
    liveDataService,
    timeService,
    userStatusService,
    ObservableModel
) {
    'use strict';

    var SYSTEM_PRESENCE_IDS = userStatusService.SYSTEM_PRESENCES;

    function mapRoutingToUserStatus (routingStatus) {
        if (!routingStatus) {
            return;
        }

        var userStatus = userStatusService.findRoutingStatus(routingStatus.status());
        if (userStatus && routingStatus.startTime) {
            return _.assign({dateModified: routingStatus.startTime()}, userStatus);
        } else {
            return _.clone(userStatus);
        }
    }

    function mapPresenceToUserStatus (presence) {
        if (!presence) {
            return;
        }

        var userStatus;
        if (presence.presenceDefinition) {
            userStatus = userStatusService.findPresence(presence.presenceDefinition().id());
        }

        var dateModified = presence.modifiedDate ? presence.modifiedDate() : undefined;
        if (userStatus) {
            return _.assign({dateModified: dateModified}, userStatus);
        }
    }

    function mapSecondaryPresenceToUserStatus (presence, key) {
        if (!key || !presence) {
            return;
        }

        var userStatus;
        if (SYSTEM_PRESENCE_IDS[key]) {
            userStatus = userStatusService.findPresence(SYSTEM_PRESENCE_IDS[key]);
        }

        var dateModified = presence.modifiedDate ? presence.modifiedDate() : undefined;
        if (userStatus) {
            return _.assign({dateModified: dateModified}, userStatus);
        }
    }

    var User = ObservableModel.extend({
        defaults: {
            stationUri: null,
            images: [],
            routingStatus: {
                status: undefined,
                startTime: undefined
            },
            presence: {},
            outOfOffice: {
                startDate: undefined,
                active: undefined
            },
            division: {}
        }
    }, function () {
        var self = this;

        /**
         * A map of images available to for the user. This method will return an object
         * map whose keys are the resolutions for the image and the value is the URI to
         * the image. For example:
         *
         *   {
         *     x48: http://purecloud.com/path/to/image
         *   }
         *
         * Current supported resolution keys are:
         *
         *   - x48
         *   - x96
         *   - x128
         *   - x200
         *   - x300
         *   - x400
         *
         * The number in the key names indicate the height and width of the image that
         * will be sent back from the provided URI. If an image is not available for a
         * resolution, the URI will be an empty string.
         */
        self.avatarImages = ko.computed(function () {
            var imageMap = {
                x48: 'no_img',
                x96: 'no_img',
                x128: 'no_img',
                x200: 'no_img',
                x300: 'no_img',
                x400: 'no_img'
            };

            _.forEach(self.images(), function (imgObj) {
                imageMap[imgObj.resolution] = imgObj.imageUri;
            });

            return imageMap;
        });

        self.primaryPresence = ko.computed(function () {
            return self.presence();
        });

        self.isOffline = ko.computed(function () {
            return self.presence() &&
                self.presence().presenceDefinition &&
                self.presence().presenceDefinition().id() == SYSTEM_PRESENCE_IDS.OFFLINE;
        });

        self.isOnQueue = ko.computed(function () {
            return self.presence() &&
                self.presence().presenceDefinition &&
                self.presence().presenceDefinition().id() == SYSTEM_PRESENCE_IDS.ON_QUEUE;
        });

        self.isOutOfOffice = ko.computed(function () {
            return self.outOfOffice() && self.outOfOffice().active();
        });

        self.isNotResponding = ko.computed(function () {
            return self.isOnQueue() && self.routingStatus() && self.routingStatus().status() === 'NOT_RESPONDING';
        });

        /**
         * Get the amount of time that the user has spent in the current presence state.
         *
         * @return {Integer} The amount of time, in milliseconds, that the user has spent
         *  in their current presence status.
         */
        self.timeInPresence = function () {
            // This method is specifically designed to NOT be a computed since it is
            // expected to be called frequently to determine the total time spent in the
            // current status.
            if (self.presence() &&
                self.presence().modifiedDate &&
                self.presence().modifiedDate() &&
                timeService.getTime()) {
                var now = timeService.getTime();
                return now.diff(self.presence().modifiedDate());
            }

            return 0;
        };

        self.timeInRoutingStatus = function () {
            // This method is specifically designed to NOT be a computed since it is
            // expected to be called frequently to determine the total time spent in the
            // current status.
            if (self.routingStatus() && self.routingStatus().startTime() && timeService.getTime()) {
                var now = timeService.getTime();
                return now.diff(self.routingStatus().startTime());
            }

            return 0;
        };

        self.secondaryPresenceUserStatus = ko.computed(function () {
            if (self.presence() &&
                self.presence().presenceDefinition &&
                self.presence().presenceDefinition().id()) {
                var status = mapPresenceToUserStatus(self.presence());
                if (status && status.primaryPresence) {
                    return status;
                }
            }
            return null;
        });

        self.displayStatus = ko.computed(function () {
            var status;
            if (userStatusService.initialized()) {
                if (self.isOutOfOffice() && self.isOffline()) {
                    // If we're OOO, but offline, show OOO offline status
                    status = userStatusService.findOOOStatus('OFFLINE');
                } else if (self.isOutOfOffice()) {
                    // If we're OOO, but not offline, show OOO online status
                    status = userStatusService.findOOOStatus('ONLINE');
                } else if (((self.isOnQueue() && self.routingStatus() && self.routingStatus().status() !== 'IDLE')) ||
                    !self.presence()) {
                    // If we are on queue or don't know presence, prefer the routingStatus
                    status = mapRoutingToUserStatus(self.routingStatus()) || mapPresenceToUserStatus(self.presence());
                } else {
                    // Otherwise, just use presence
                    status = mapPresenceToUserStatus(self.presence());
                }

                if (status && status.primaryPresence && self.presence()) {
                    status = mapSecondaryPresenceToUserStatus(self.presence(), status.primaryPresence.toUpperCase());
                }
            }

            if (!status) {
                status = userStatusService.getUnknownStatus();
            }

            return new ObservableModel(status);
        });

        // TODO: remove after WFM-2291 goes into production
        self.status = ko.computed(function () {
            return self.displayStatus();
        });

        /**
         * Get the amount of time that the user has spent in the current status.
         *
         * @return {Integer} The amount of time, in milliseconds, that the user has spent
         *  in their current status.
         */
        self.timeInStatus = function () {
            // This method is specifically designed to NOT be a computed since it is
            // expected to be called frequently to determine the total time spent in the
            // current status.
            if (self.displayStatus() && self.displayStatus().dateModified && self.displayStatus().dateModified() && timeService.getTime()) {
                var now = timeService.getTime();
                return now.diff(self.displayStatus().dateModified());
            }

            return null;
        };

        self.userImageError = ko.computed(function () {
            return !self.avatarImages() || self.avatarImages().x48 === 'no_img';
        });

        self.unwrap = function () {
            return  ObservableModel.unwrap(self, [
                'avatarImages',
                'isOnQueue',
                'status',
                'timeInPresence',
                'timeInRoutingStatus',
                'timeInStatus',
                'unwrap',
                'userImageError'
            ]);
        };

    });

    liveDataService.registerTopic('users', User);
    liveDataService.registerTopic('routingStatus', User);
    liveDataService.registerTopic('status', User);

    return User;
});

